import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

export interface FactionSatisfactionBarProps {
  satisfaction: number // 0-100
  size?: 'sm' | 'md' | 'lg'
  showThresholds?: boolean
  showLabel?: boolean
  animated?: boolean
  className?: string
}

interface SatisfactionLevel {
  colorClass: string
  bgClass: string
  label: string
  description: string
}

function getSatisfactionLevel(satisfaction: number): SatisfactionLevel {
  if (satisfaction >= 70) {
    return {
      colorClass: 'text-green-500',
      bgClass: 'bg-green-500',
      label: 'Content',
      description: 'The faction is satisfied with party direction',
    }
  }
  if (satisfaction >= 40) {
    return {
      colorClass: 'text-yellow-500',
      bgClass: 'bg-yellow-500',
      label: 'Concerned',
      description: 'The faction has some concerns but remains loyal',
    }
  }
  if (satisfaction >= 20) {
    return {
      colorClass: 'text-orange-500',
      bgClass: 'bg-orange-500',
      label: 'Demanding',
      description: 'The faction is dissatisfied and may issue demands',
    }
  }
  if (satisfaction >= 1) {
    return {
      colorClass: 'text-red-500',
      bgClass: 'bg-red-500',
      label: 'Threatening',
      description: 'The faction is threatening to leave the party',
    }
  }
  return {
    colorClass: 'text-red-700',
    bgClass: 'bg-red-700',
    label: 'Defecting',
    description: 'The faction is about to defect from the party',
  }
}

const SIZE_CONFIG = {
  sm: {
    barHeight: 'h-1.5',
    thresholdHeight: 'h-2.5',
    fontSize: 'text-[10px]',
    labelWidth: 'w-6',
  },
  md: {
    barHeight: 'h-2',
    thresholdHeight: 'h-3',
    fontSize: 'text-xs',
    labelWidth: 'w-8',
  },
  lg: {
    barHeight: 'h-3',
    thresholdHeight: 'h-4',
    fontSize: 'text-sm',
    labelWidth: 'w-10',
  },
}

// Threshold markers at key satisfaction levels
const THRESHOLDS = [
  { value: 70, label: 'Safe' },
  { value: 40, label: 'Concern' },
  { value: 20, label: 'Danger' },
]

export function FactionSatisfactionBar({
  satisfaction,
  size = 'md',
  showThresholds = false,
  showLabel = true,
  animated = true,
  className,
}: FactionSatisfactionBarProps) {
  const clampedValue = Math.max(0, Math.min(100, satisfaction))
  const level = getSatisfactionLevel(clampedValue)
  const sizeConfig = SIZE_CONFIG[size]

  const barContent = (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Progress bar container */}
      <div
        className={cn(
          'relative flex-1 overflow-hidden rounded-full bg-muted',
          sizeConfig.barHeight
        )}
      >
        {/* Filled portion */}
        <div
          className={cn(
            'h-full rounded-full',
            level.bgClass,
            animated && 'transition-all duration-500 ease-out',
            // Pulse effect at critical levels
            animated && clampedValue <= 20 && clampedValue > 0 && 'animate-pulse'
          )}
          style={{ width: `${clampedValue}%` }}
        />

        {/* Threshold markers */}
        {showThresholds &&
          THRESHOLDS.map((threshold) => (
            <div
              key={threshold.value}
              className={cn(
                'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 bg-border/60',
                sizeConfig.thresholdHeight
              )}
              style={{ left: `${threshold.value}%` }}
              title={`${threshold.label} threshold (${threshold.value})`}
            />
          ))}
      </div>

      {/* Numeric label */}
      {showLabel && (
        <span
          className={cn(
            'font-medium tabular-nums text-right shrink-0',
            sizeConfig.fontSize,
            sizeConfig.labelWidth,
            level.colorClass
          )}
        >
          {clampedValue}
        </span>
      )}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{barContent}</div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-center">
          <div className="font-medium">
            {level.label}: {clampedValue}/100
          </div>
          <div className="text-muted-foreground">{level.description}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
